import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Typography, Stack, TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useSalonContextStore } from '../../store/salonContextStore';
import { profitabilityApi } from '../../services/employeeService';
import { GroupedBarChart, LineChart } from '../../components/charts/SimpleCharts';
import {
  PageHeader, SectionCard, StatTile, EmptyState, LoadingState, StatusPill, PersonAvatar,
} from '../../components/ui';
import { CHART_PALETTE, tokens } from '../../theme';
import { formatMoney, formatMonthYear, formatNumber, formatPercent } from '../../utils/format';

interface Row {
  staffId: string; name: string; revenue: number; directCost: number; result: number; profitabilityPct: number; revenuePerHour: number; clientsCount: number;
}
interface YearPoint { month: number; revenue: number; directCost: number; result: number; profitabilityPct: number; servicesRevenue: number; productsRevenue: number; materialsCost: number; revenuePerHour: number; clientsCount: number; }

const MONTHS = ['Sty', 'Lut', 'Mar', 'Kwi', 'Maj', 'Cze', 'Lip', 'Sie', 'Wrz', 'Paź', 'Lis', 'Gru'];
const COLOR_REVENUE = CHART_PALETTE[0];
const COLOR_RESULT = CHART_PALETTE[1];
const COLOR_COST = CHART_PALETTE[4];

const loadErrorMessage = (e: unknown) => ((e as { response?: { status?: number } }).response?.status === 403
  ? 'Twoje konto nie ma dostępu do tych danych. Poproś właściciela salonu o nadanie uprawnień.'
  : 'Sprawdź połączenie i odśwież stronę.');

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const num = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' } as const;

export const ProfitabilityPage: React.FC = () => {
  const { activeSalonId } = useSalonContextStore();
  const now = new Date();
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selected, setSelected] = useState<{ name: string; series: YearPoint[] } | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const yearRef = useRef<HTMLDivElement | null>(null);

  const load = useCallback(async () => {
    if (!activeSalonId) return;
    setLoading(true);
    const [y, m] = month.split('-').map(Number);
    setLoadError(null);
    try { setRows(await profitabilityApi.salonMonth(activeSalonId, y, m)); }
    catch (e) { setRows([]); setLoadError(loadErrorMessage(e)); }
    finally { setLoading(false); }
  }, [activeSalonId, month]);
  useEffect(() => { load(); }, [load]);

  const openYear = async (r: Row) => {
    const [y] = month.split('-').map(Number);
    const series = await profitabilityApi.staffYear(r.staffId, y);
    setSelected({ name: r.name, series });
    setSelectedId(r.staffId);
  };

  useEffect(() => {
    if (selected) yearRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [selected]);

  const [yy, mm] = month.split('-').map(Number);
  const monthLabel = month ? capitalize(formatMonthYear(new Date(yy, mm - 1, 1))) : '';
  const totalRevenue = rows.reduce((s, r) => s + r.revenue, 0);
  const totalCost = rows.reduce((s, r) => s + r.directCost, 0);
  const totalResult = rows.reduce((s, r) => s + r.result, 0);
  const totalPct = totalRevenue > 0 ? (totalResult / totalRevenue) * 100 : 0;

  return (
    <Box>
      <PageHeader
        breadcrumbs={[{ label: 'Analizy' }]}
        title="Rentowność pracowników"
        description="Ile obrotu przynosi każda osoba i ile kosztuje salon. Kliknij pracownika, aby zobaczyć cały rok."
      >
        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
          <TextField
            type="month"
            size="small"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            inputProps={{ 'aria-label': 'Miesiąc' }}
            sx={{ width: 190 }}
          />
        </Stack>
      </PageHeader>

      {loading ? (
        <Stack gap={2.5}>
          <LoadingState variant="tiles" count={4} />
          <LoadingState variant="block" height={220} />
        </Stack>
      ) : loadError ? (
        <SectionCard>
          <EmptyState icon={<LockOutlinedIcon />} title="Nie udało się wczytać rentowności" description={loadError} />
        </SectionCard>
      ) : rows.length === 0 ? (
        <SectionCard>
          <EmptyState
            icon={<InsightsOutlinedIcon />}
            title="Brak danych za ten miesiąc"
            description="Rentowność pojawi się, gdy pracownicy będą mieli w tym miesiącu sprzedaż i naliczone wynagrodzenia. Wybierz inny miesiąc."
          />
        </SectionCard>
      ) : (
        <Stack gap={2.5}>
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' } }}>
            <StatTile label="Obrót zespołu" value={formatMoney(totalRevenue, { decimals: 0 })} hint={monthLabel} />
            <StatTile label="Koszty bezpośrednie" value={formatMoney(totalCost, { decimals: 0 })} hint="Wynagrodzenia, składki, materiały" />
            <StatTile
              label="Wynik"
              value={formatMoney(totalResult, { decimals: 0 })}
              valueColor={totalResult >= 0 ? tokens.success : tokens.danger}
              hint="Obrót minus koszty"
            />
            <StatTile label="Rentowność" value={formatPercent(totalPct, 1)} valueColor={totalPct < 0 ? tokens.danger : undefined} hint="Wynik jako procent obrotu" />
          </Box>

          <SectionCard title="Obrót i koszt według pracowników" subtitle={monthLabel}>
            <GroupedBarChart
              height={240}
              categories={rows.map((r) => r.name.split(' ')[0])}
              series={[
                { label: 'Obrót', color: COLOR_REVENUE, values: rows.map((r) => r.revenue) },
                { label: 'Koszt', color: COLOR_COST, values: rows.map((r) => r.directCost) },
              ]}
              formatValue={(v) => formatMoney(v, { decimals: 0 })}
            />
          </SectionCard>

          <SectionCard title="Pracownicy" subtitle="Kliknij wiersz, aby zobaczyć rentowność w kolejnych miesiącach" padding="none">
            <TableContainer>
              <Table sx={{ minWidth: 820 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Pracownik</TableCell>
                    <TableCell align="right">Obrót</TableCell>
                    <TableCell align="right">Koszt</TableCell>
                    <TableCell align="right">Wynik</TableCell>
                    <TableCell align="right">Rentowność</TableCell>
                    <TableCell align="right">Obrót na godzinę</TableCell>
                    <TableCell align="right">Klienci</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow
                      key={r.staffId}
                      hover
                      selected={selectedId === r.staffId}
                      tabIndex={0}
                      sx={{ cursor: 'pointer' }}
                      onClick={() => openYear(r)}
                      onKeyDown={(e) => { if (e.key === 'Enter') openYear(r); }}
                    >
                      <TableCell>
                        <Stack direction="row" alignItems="center" gap={1.25}>
                          <PersonAvatar fallback={r.name} firstName={r.name.split(' ')[0]} lastName={r.name.split(' ').slice(1).join(' ')} seed={r.staffId} size={32} />
                          <Typography sx={{ fontWeight: 600, fontSize: 'inherit', whiteSpace: 'nowrap' }}>{r.name}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell align="right" sx={num}>{formatMoney(r.revenue, { decimals: 0 })}</TableCell>
                      <TableCell align="right" sx={num}>{formatMoney(r.directCost, { decimals: 0 })}</TableCell>
                      <TableCell align="right" sx={{ ...num, fontWeight: 650, color: r.result >= 0 ? 'success.main' : 'error.main' }}>{formatMoney(r.result, { decimals: 0 })}</TableCell>
                      <TableCell align="right">
                        <StatusPill size="small" tone={r.profitabilityPct >= 0 ? 'success' : 'danger'} label={formatPercent(r.profitabilityPct, 1)} />
                      </TableCell>
                      <TableCell align="right" sx={num}>{formatMoney(r.revenuePerHour, { decimals: 0 })}</TableCell>
                      <TableCell align="right" sx={num}>{formatNumber(r.clientsCount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </SectionCard>

          {selected && (
            <Box ref={yearRef} sx={{ scrollMarginTop: 88 }}>
              <SectionCard
                title={`${selected.name} — rok ${yy}`}
                subtitle="Obrót, koszt i wynik w kolejnych miesiącach"
                action={(
                  <Tooltip title="Zamknij wykres">
                    <IconButton onClick={() => { setSelected(null); setSelectedId(null); }} aria-label="Zamknij wykres"><CloseIcon /></IconButton>
                  </Tooltip>
                )}
              >
                <LineChart
                  height={240}
                  categories={selected.series.map((s) => MONTHS[s.month - 1])}
                  series={[
                    { label: 'Obrót', color: COLOR_REVENUE, values: selected.series.map((s) => s.revenue) },
                    { label: 'Koszt', color: COLOR_COST, values: selected.series.map((s) => s.directCost) },
                    { label: 'Wynik', color: COLOR_RESULT, values: selected.series.map((s) => s.result) },
                  ]}
                  formatValue={(v) => formatMoney(v, { decimals: 0 })}
                />
              </SectionCard>
            </Box>
          )}
        </Stack>
      )}
    </Box>
  );
};

export default ProfitabilityPage;
